import { FlightModel } from 'api-hooks/tour/tour-list/flight/model';
import { TourPriceModel } from 'api-hooks/tour/tour-list/tour-detail';
import { Value } from 'baseui/select';
import stylesColors from 'common/styles/styles.colors';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import {
  ActionType,
  ColumnDataType,
  IColumn,
} from 'components/elements/table/type';
import getError from 'components/elements/table/utils/get-error';
import { SeparatorVertical } from 'components/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { TourSaleItemGuestFormType } from '../form-type';

interface Props {
  parentIndex: number;
}

function useGetTourParticipantDetailTable(): IColumn<TourSaleItemGuestFormType>[] {
  const { t } = useTranslation();
  const { control } = useFormContext();
  const [enumOptions, scheduleOptions, typeOptions] = useWatch({
    control,
    name: ['enumOptions', 'scheduleOptions', 'typeOptions'],
  });

  return React.useMemo(() => {
    return [
      {
        header: t('common:type'),
        accessorKey: 'tourPriceId',
        minSize: 100,
        dataType: ColumnDataType.select,
        options: typeOptions,
        required: true,
      },
      {
        header: t('common:bed'),
        accessorKey: 'bedType',
        minSize: 100,
        dataType: ColumnDataType.select,
        options: enumOptions,
        required: true,
      },
      {
        header: t('common:note'),
        accessorKey: 'note',
        minSize: 100,
        dataType: ColumnDataType.text,
      },
      {
        header: t('common:group'),
        accessorKey: 'groupNumber',
        minSize: 100,
        dataType: ColumnDataType.text,
        required: true,
      },
      {
        header: t('common:schedule'),
        accessorKey: 'tourFlightId',
        minSize: 100,
        dataType: ColumnDataType.select,
        options: scheduleOptions,
      },
      {
        header: t('common:price'),
        accessorKey: 'price',
        minSize: 100,
        dataType: ColumnDataType.number,
        minNumber: 0,
      },
    ];
  }, [enumOptions, scheduleOptions, t, typeOptions]);
}

export default function TourParticipantDetailTable(props: Props) {
  const { parentIndex } = props;
  const { editable } = useFormState();

  const { setValue, formState, getValues, clearErrors } = useFormContext();

  const columns = useGetTourParticipantDetailTable();

  const name = `tourSales.0.tourSaleGuests` as const;

  const tourSaleGuests = getValues(name);

  const tourSaleGuest = tourSaleGuests[parentIndex];

  const dispatch = React.useCallback(
    (action) => {
      const columnId = action.columnId as keyof TourSaleItemGuestFormType;
      const type = action.type as ActionType;
      const value = action.value;
      const extra = action.extra as Value;

      const otherVal: () => Partial<TourSaleItemGuestFormType> = () => {
        if (columnId === 'tourPriceId') {
          const tourPrice = extra?.[0]?.extra as TourPriceModel | undefined;
          const price = tourPrice?.price || tourSaleGuest?.price;
          return {
            tourPriceRefId: tourPrice?.refId || '',
            type: tourPrice?.type || '',
            price,
          };
        }
        if (columnId === 'tourFlightId') {
          const tourFlight = extra?.[0]?.extra as FlightModel | undefined;

          return {
            flightName: tourFlight?.name || '',
            tourFlightRefId: tourFlight?.refId || '',
          };
        }
        return {};
      };

      if (type === 'update_cell') {
        setValue(
          `${name}.${parentIndex}`,
          {
            ...tourSaleGuest,
            [columnId]: value,
            ...otherVal(),
          },
          {
            shouldTouch: true,
          },
        );
        clearErrors(`${name}.${parentIndex}.${columnId}`);
      }
    },
    [tourSaleGuest, setValue, name, parentIndex, clearErrors],
  );

  if (!tourSaleGuest) {
    return null;
  }

  return (
    <div style={{ width: '100%' }}>
      <TableInput
        columns={columns}
        data={[tourSaleGuest]}
        dispatch={dispatch}
        disabled={!editable}
        noAddRemoveRow
        name={name}
        uniqueRowKey="customId"
        onCustomBackgroundColorCell={({ accessorKey }) => {
          const fieldName = `${name}.${parentIndex}.${accessorKey}` as const;
          const hasError = getError(formState.errors, fieldName);
          if (hasError) {
            return `${stylesColors.redLightest}80`;
          }
          return '';
        }}
      />

      <SeparatorVertical $margin={32} />
    </div>
  );
}
